import ConfirmDialog from '@components/Shared/Dialog/ConfirmDialog';
import { dialogLine, dialogTray, formDialogTextStyles } from '@components/Shared/Dialog/formDialog.styles';
import { useLocalization } from '@lib/localization';
import { Box, Typography } from '@mui/material';
import { type User } from '@store/api/userEndpoints';
import { Trash2 } from 'lucide-react';
import UserAvatar from './UserAvatar';

interface DeleteUsersDialogProps {
  open: boolean;
  /** Kept by the caller while the dialog closes, so the names stay put as the selection is let go. */
  users: User[];
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

/**
 * Asks before the accounts picked on the Users page are deleted, naming each of them, and says
 * once what goes with them.
 */
const DeleteUsersDialog = ({ open, users, onClose, onConfirm }: DeleteUsersDialogProps) => {
  const { t } = useLocalization();

  return (
    <ConfirmDialog
      open={open}
      title={t('User_DeleteMany_Title', { count: users.length })}
      icon={<Trash2 size={16} />}
      confirmLabel={t('Common_Delete')}
      confirmColor="error"
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Box component="ul" sx={listStyles}>
        {users.map((user) => (
          <Box component="li" key={user.id} sx={rowStyles}>
            <UserAvatar name={user.name} size={24} />
            <Typography noWrap sx={{ minWidth: 0, fontSize: 14, fontWeight: 500 }}>
              {user.name}{' '}
              <Typography component="span" color="text.secondary" sx={{ fontSize: 13 }}>@{user.username}</Typography>
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={formDialogTextStyles}>{t('User_DeleteMany_Content')}</Typography>
    </ConfirmDialog>
  );
};

// The accounts in a well a shade darker than the dialog, as the single sign-on changes are listed.
const listStyles = {
  maxHeight: 240,
  overflowY: 'auto',
  m: 0,
  mb: 2,
  p: 1.25,
  listStyle: 'none',
  border: '1px solid',
  borderColor: dialogLine,
  borderRadius: '8px',
  backgroundColor: dialogTray,
};

const rowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  '&:not(:last-of-type)': {
    pb: 1,
    mb: 1,
    borderBottom: '1px solid',
    borderColor: dialogLine,
  },
};

export default DeleteUsersDialog;
